import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { Button } from "app/components/ui/button";
import { ScrollArea } from "app/components/ui/scroll-area";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "app/components/ui/dialog";
import { useGlobalSlice } from "app/slice";
import { RootState } from "types";

// Types for API response
interface Customer {
  id: string;
  networkType: number;
  networkTypeName: string;
  numberOfLines: number;
}

interface CurrentSecurityPlan {
  id: string;
  name: string;
  frequency: number;
  lines: number[];
  networkType: string;
}

interface AvailableUpgradePlan {
  id: string;
  name: string;
  nameNum: number;
  price: string;
  frequency: number;
  lines: number[];
  networkType: string;
  stripePriceId: string;
  tier: string[];
  isCurrentPlan: boolean;
}

interface SecurityPlansResponse {
  customer: Customer;
  currentSecurityPlan: CurrentSecurityPlan;
  availableUpgradePlans: AvailableUpgradePlan[];
}

interface ApiResponse {
  status: number;
  statusText: string;
  data: SecurityPlansResponse;
  meta: {
    requestId: string;
    url: string;
  };
}

// Types for transformed component data
interface TransformedPlan {
  id: string;
  name: string;
  price: string;
  frequency: number;
  lines: number[];
  networkType: string;
  stripePriceId?: string;
  tier?: string[];
  features: string[];
  isSelectedPlan: boolean;
}

interface UpgradeSecurityPlanSharedProps {
  triggerClassName?: string;
  triggerText?: string;
  fromPage?: string;
  title?: string;
}

export const UpgradeSecurityPlanShared = ({ 
  triggerClassName = "inline-block text-[#FE4D01] text-sm sm:text-[0.925rem] lg:text-base font-semibold leading-4 sm:leading-5 lg:leading-6 cursor-pointer underline decoration-solid decoration-skip-ink decoration-[auto] underline-offset-[auto] [text-underline-position:from-font]",
  triggerText = "Upgrade", // triggerText = "Upgrade",
  fromPage = "upgrade-security-plan",
  title,
}: UpgradeSecurityPlanSharedProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { useLazyGetDigitalSecurityCurrentAndUpgradePlansQuery } = useGlobalSlice();
  const user = useSelector((state: RootState) => state.global?.user);
  
  const [getDigitalSecurityPlans, { data, isLoading, isError, error }] = useLazyGetDigitalSecurityCurrentAndUpgradePlansQuery();

  useEffect(() => {
    if (isOpen && user?._id) {
      // Call the lazy query with user ID when dialog opens
      getDigitalSecurityPlans({ customerId: user._id });
      console.log("Fetching digital security plans...");
    }
  }, [isOpen, getDigitalSecurityPlans, user?._id]);

  console.log("Digital Security Plans Data:", data);
  console.log("Loading:", isLoading);
  console.log("Error:", isError, error);

  // Transform API data to match component structure
  const transformPlansData = (): TransformedPlan[] => {
    if (!data?.data) return [];
    
    const { currentSecurityPlan, availableUpgradePlans } = data.data as SecurityPlansResponse;
    const allPlans: TransformedPlan[] = [];
    
    console.log("Current Security Plan:", currentSecurityPlan);
    console.log("Available Upgrade Plans:", availableUpgradePlans);
    
    // Add current plan
    if (currentSecurityPlan) {
      allPlans.push({
        id: currentSecurityPlan.id,
        name: currentSecurityPlan.name,
        price: 'Current Plan', // This will be overridden if we find a matching upgrade plan with actual price
        frequency: currentSecurityPlan.frequency,
        lines: currentSecurityPlan.lines,
        networkType: currentSecurityPlan.networkType,
        features: [
          "Dark web monitoring",
          "Password manager", 
          "Data broker removal",
          "$1M identity theft insurance",
          "Self-activation for privacy control"
        ],
        isSelectedPlan: true,
      });
    }
    
    // First, try to find a matching upgrade plan for the current plan to get its actual price
    let currentPlanPrice = 'Current Plan';
    if (currentSecurityPlan && availableUpgradePlans) {
      const matchingUpgradePlan = availableUpgradePlans.find(upgradePlan => 
        upgradePlan.name === currentSecurityPlan.name && 
        upgradePlan.price !== '$0'
      );
      if (matchingUpgradePlan) {
        currentPlanPrice = matchingUpgradePlan.price;
      }
    }

    // Update current plan with actual price if found
    if (currentSecurityPlan && allPlans.length > 0) {
      allPlans[0].price = currentPlanPrice;
    }

    // Add available upgrade plans (filter out plans with price $0)
    availableUpgradePlans?.forEach((plan: AvailableUpgradePlan) => {
            
      // Skip plans with price $0
      if (plan?.price === '$0') {
        return;
      }
      
      allPlans.push({
        id: plan.id,
        name: plan.name,
        price: plan?.price,
        frequency: plan.frequency,
        lines: plan.lines,
        networkType: plan.networkType,
        stripePriceId: plan.stripePriceId,
        tier: plan.tier,
        features: [
          "Credit monitoring (all 3 bureaus)",
          "Financial transaction alerts",
          "Home title monitoring",
          "Spam call & text blocking",
          "VPN & antivirus"
        ],
        isSelectedPlan: false,
      });
    });
    
    console.log("Final transformed plans:", allPlans);
    return allPlans;
  };

  const handleBuyPlan = (plan: TransformedPlan) => {
    if (!plan.price || plan.price === '$0' || plan.isSelectedPlan) {
      return; // Don't navigate for free or selected plans
    }

    // Prepare the location state for checkout
    const locationState = {
      from: fromPage,
      customerData: user,
      planDetails: {
        securityPlan: {
          id: plan.id,
          name: plan.name,
          price: plan.price,
          frequency: plan.frequency,
          lines: plan.lines,
          networkType: plan.networkType,
          stripePriceId: plan.stripePriceId,
          tier: plan.tier,
          features: plan.features,
        },
        type: "security-plan-upgrade",
      },
      securityPlanData: {
        planId: plan.id,
        stripePriceId: plan.stripePriceId,
        price: plan.price,
        frequency: plan.frequency,
        name: plan.name,
      },
    };

    console.log("Navigating to checkout with security plan:", locationState);
    
    // Navigate to checkout with the plan details
    navigate("/checkout", {
      state: locationState
    });
    
    // Close the dialog
    setIsOpen(false);
  };

  const securityPlans = transformPlansData();

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <span className={triggerClassName}>
          {triggerText}
        </span>
      </DialogTrigger>
      <DialogContent
        className="top-1/2 gap-0 bg-[#F6F6F6] w-[95%] sm:max-w-[93.5rem] border border-[#E5E7EB] rounded-xl md:rounded-2xl"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="flex-row items-center justify-between bg-transparent p-4 sm:p-5 lg:p-6">
          <DialogTitle className="text-xl font-bold leading-5 text-black capitalize sm:leading-6 md:leading-7 lg:text-2xl font-mobile">
            {title}
          </DialogTitle>
          <DialogClose className="rounded-full">
            <img
              src="/assets/close-circle.webp"
              className="flex-shrink-0 rounded-full size-5 md:size-6 opacity-90 hover:opacity-100"
              alt="Close"
            />
          </DialogClose>
        </DialogHeader>
        <ScrollArea className="h-[26.5rem] sm:h-[30rem] md:h-[36rem] lg:h-[38.4065rem] max-h-[80vh] px-4 sm:px-5 lg:px-6">
          {isLoading ? (
            <div className="flex justify-center items-center h-full">
              <p>Loading security plans...</p>
            </div>
          ) : isError ? (
            <div className="flex justify-center items-center h-full">
              <p>Error loading security plans: {error as string || 'Unknown error'}</p>
            </div>
          ) : securityPlans.length === 0 ? (
            <div className="flex justify-center items-center h-full">
              <p>No security plans available</p>
            </div>
          ) : (
            <div className="flex flex-wrap justify-center w-full gap-5 sm:gap-[1.125rem] lg:gap-6 pb-5 sm:pb-6 lg:pb-[1.81rem]">
              {securityPlans.map((plan: TransformedPlan, index: number) => {
                console.log(`Rendering plan: ${plan.name}, isSelectedPlan: ${plan.isSelectedPlan}, price: ${plan.price}`);
                return (
                <div key={plan.id || index} className="bg-white w-full max-w-[21.5625rem] rounded-lg sm:rounded-xl overflow-hidden shadow-sm">
                  <div className="p-4 sm:p-5 md:p-6 lg:p-[1.625rem]">
                    <h4 className="text-black font-mobile text-[1.375rem] sm:text-2xl md:text-[1.625rem] font-medium leading-7 sm:leading-9 md:leading-[2.625rem] mb-2 sm:mb-3 md:mb-[0.815rem]">
                      {plan.name}
                    </h4>
                    <div className="flex gap-1 mb-3 sm:mb-4 md:mb-6 lg:mb-7">
                      {plan.isSelectedPlan && plan.price === 'Current Plan' ? (
                        <p className="text-[#101828] font-mobile text-2xl sm:text-3xl md:text-4xl font-bold leading-8 sm:leading-9 md:leading-10 tracking-[-0.06rem]">
                          Current Plan
                        </p>
                      ) : (
                        <>
                          <p className="text-[#101828] font-mobile text-4xl sm:text-[2.5rem] md:text-5xl font-bold leading-10 sm:leading-[3.25rem] md:leading-[3.75rem] tracking-[-0.06rem]">
                            {plan.price}
                          </p>
                          <span className="inline-block text-[#667085] text-sm sm:text-[0.925rem] md:text-base font-medium leading-4 sm:leading-5 md:leading-6 mt-4 sm:mt-5 md:mt-6">
                            /per {plan.frequency === 12 ? 'year' : 'month'}
                          </span>
                        </>
                      )}
                    </div>
                    <ul className="mb-4 space-y-2.5 sm:space-y-3 md:space-y-4 lg:space-y-5 sm:mb-5 md:mb-7 lg:mb-9">
                      {plan.features.map((feature: string, featureIndex: number) => (
                        <li key={featureIndex} className="flex items-start gap-[0.375rem] sm:gap-[0.45rem] md:gap-[0.56rem]">
                          <span className="flex-shrink-0 mt-[0.1rem] sm:mt-[0.2rem] md:mt-[0.1rem] text-[#1EC677]">
                            <svg
                              className="w-4 h-4 sm:w-[1.125rem] sm:h-[1.125rem] md:w-5 md:h-5"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M2 12H22M22 12L15 7M22 12L15 17"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                          <p className="text-[#1D1D1B] text-sm sm:text-[0.925rem] md:text-base font-normal leading-5 sm:leading-6 md:leading-relaxed">
                            {feature}
                          </p>
                        </li>
                      ))}
                    </ul>
                    <Button
                      type="button"
                      variant="primary"
                      disabled={!plan.price || plan.isSelectedPlan}
                      onClick={() => handleBuyPlan(plan)}
                      className={`capitalize h-11 sm:h-[2.875rem] md:h-[2.875rem] md:h-12 lg:h-[3.125rem] rounded-[2.4375rem] shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] ${plan.isSelectedPlan ? 'bg-[#D4D4D4] hover:bg-[#D4D4D490] text-black' : plan.price && plan.price !== '$0' ? 'bg-[#FE4D01] text-white' : 'bg-[#D4D4D4] text-gray-500 cursor-not-allowed'}`}
                    >
                      {plan.isSelectedPlan ? 'Selected Plan' : plan.price && plan.price !== '$0' ? 'Buy Plan' : 'Plan Unavailable'}
                    </Button>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
